import React, {
	useCallback, useEffect,
	useRef,
	useState
} from 'react'

import { ProfileCameraIcon, } from '../../assets/svg/icons'
import { useCustomSelectors } from '../../services/selectors/allSelectors'
import { ProfilePicLoader } from './loadingView'

import debounce from 'lodash/debounce'
import { onHandleProfilePicChanger } from '../../assets/js/custome'

import * as constants from '../../constants/workspace'
import { navItemAllowed } from '../../constants/moduleSubItems'
import useMyModules, { buildModuleGate } from '../../hooks/useMyModules'
import { useAdminStealthHidden } from '../../hooks/useAdminStealth'
import * as Hooks from '../../hooks'
import * as routeNames from '../../routes/routeName'
import * as pharmacyTeamNotes from '../../services/actions/pharmacy/other'
import * as useEffectHelpers from '../../utils/helper/useEffectsHelper'
import NewRxCard from '../newRxCard/NewRxCard'

const TabsSubMenuList = ({ data }) => {

	const navigate = Hooks.useCustomNavigate()
	const location = Hooks.useCustomLocation()
	const { adminModule, profileData } = useCustomSelectors()
	const currentRoute = `${location.pathname}${location.search || ''}`
	// ADMIN SCREEN PRIVACY (F9 x5, see useAdminStealth). The pharmacist-only
	// nav entries are role entries just like the admin ones, so they hide with
	// them: while hidden this reads false and every check below draws the menu
	// an ordinary team member sees. Deliberately scoped to NAV VISIBILITY --
	// profileData.type itself is untouched, so pharmacist logic elsewhere
	// (verification, video calls, signing) behaves exactly as it does today.
	// `adminStealthHidden` is false for every non-admin and false whenever the
	// backend cannot answer, so this is a no-op for everybody else.
	const adminStealthHidden = useAdminStealthHidden()
	const isPharmacistUser = profileData?.type === 'pharmacist' && !adminStealthHidden

	// The sidebar is the SECOND nav surface: for most tabs (Compound, Forms,
	// FDA, Travel, App, Team, ...) it is the only place their sub-items are
	// listed, so the sub-module gate has to run here too. FAIL-OPEN: in legacy
	// mode (modules === null) navItemAllowed always passes and the list is
	// exactly today's. Items with no sub-module key of their own are never
	// hidden here — their tab's own gate still covers them.
	const { hasKey } = buildModuleGate(useMyModules())

	return (<>
		<ul className="pt-2 pb-4">
			{data?.map((value, index) => {
				if (value.route === routeNames.pharmacy_driver_clinic_call_logs && !isPharmacistUser) {
					return null
				}

				if (!navItemAllowed(value.route, hasKey)) {
					return null
				}

				const isActive = value.route.includes('?')
					? currentRoute === value.route
					: location.pathname === value.route

				return (<React.Fragment key={index}>
					{(value.name === 'WINRX' || value.name === 'STAT Holidays') && adminModule === 0 ?
						null
						:
						<li className="mb-2" onClick={() => { return navigate(value.route) }}>
							<button className={`${isActive ? "active" : ""}  py-2 rounded-md w-full btnnn flex items-center justify-between text-left`}>
								<span>{value.name.toUpperCase()}</span>
							</button>
						</li>}
				</React.Fragment>
				)
			})}
		</ul>
	</>)
}

const Sidebar = ({ position, showProfile = true, children, topContent }) => {

	const navigate = Hooks.useCustomNavigate()
	const dispatch = Hooks.useCustomDispatch()

	const location = Hooks.useCustomLocation()
	const fileInputRef = useRef(null);
	const imageRef = useRef(null)
	const routeLocation = location.pathname

	const [showNote, setShowNote] = Hooks.useCustomState(true)
	const [profilePicLoader, setProfilePicLoader] = Hooks.useCustomState(false)

	const [addTeamNoteData, setAddTeamNoteData] = useState('')
	const [loading, setLoading] = useState(false)

	const { profileData, pharmacyId,
		teamNotesMessageResponse, pharmacyLoginUser, blisterPatientsEnabled } = useCustomSelectors()

	// Sub-module gate for the two lists rendered inline below (Patients and
	// Compound) — the rest go through TabsSubMenuList, which gates itself.
	// FAIL-OPEN: passes everything in legacy mode.
	const { hasKey: hasModuleKey } = buildModuleGate(useMyModules())

	const teamNotesMessage = teamNotesMessageResponse?.data?.data;

	const debouncedDispatch = useCallback(
		debounce((value) => {
			dispatch(pharmacyTeamNotes.pharmacyAddTeamNotes(value));
		}, 10), [dispatch]
	);

	const [showWarning, setShowWarning] = useState(false);
	const typingTimerRef = useRef(null);
	const TYPING_TIMEOUT = 1000; // 5 seconds

	const handalDashboardDataChange = (e) => {
		const value = e.target.value;
		setAddTeamNoteData(value);

		// Clear existing timer
		if (typingTimerRef.current) {
			clearTimeout(typingTimerRef.current);
		}

		// Remove warning if shown
		setShowWarning(false);

		// Set new timer
		typingTimerRef.current = setTimeout(() => {
			setShowWarning(true);
		}, TYPING_TIMEOUT);
		setLoading(false)
	};

	useEffectHelpers.useAblySubScribeTeamNotes(pharmacyId, dispatch, setLoading, profileData)

	useEffect(() => {
		if (teamNotesMessage) {
			setAddTeamNoteData(teamNotesMessage)
		} else if (teamNotesMessage === null) {
			setAddTeamNoteData('')
		}
	}, [teamNotesMessage])

	const getStatusFromPathname = (pathname) => {
		const searchParams = new URLSearchParams(pathname.split('?')[1]);
		return searchParams.get('status');
	};

	const currentStatus = getStatusFromPathname(location.search);

	const HandleUpdateNote = () => {
		setLoading(false);
		// Clear timer and remove warning
		if (typingTimerRef.current) {
			clearTimeout(typingTimerRef.current);
		}
		setShowWarning(false);
		debouncedDispatch(addTeamNoteData);
	}

	useEffect(() => {
		const relevantRoutes = [
			routeNames.HOME_ROUTE, routeNames.DASHBOARD_ROUTE, routeNames.APPRECIATIONS_ROUTE,
			routeNames.TIMESHEET_ROUTE, routeNames.NEWREQUEST_ROUTE, routeNames.ALLREQUEST_ROUTE,
			routeNames.pharmacy_search_module, routeNames.ALLEMPLOYEES_ROUTE, routeNames.EDIT_TIME_OFFR_REQUEST_ROUTE,
			routeNames.MY_PROFILE, routeNames.pharmacy_all_notifications, routeNames.pharmacy_file_manager,
			routeNames.pharmacy_reminder_notes, routeNames.pharmacy_delivered_rx_reasons, routeNames.pharmacy_tasks_list, routeNames.pharmacy_bookmarks
		];

		if (relevantRoutes.includes(routeLocation) || routeLocation.startsWith(routeNames.EDIT_TIME_OFFR_REQUEST_ROUTE)) {
			dispatch(pharmacyTeamNotes.fetchTeamNotesMessage());
		}
	}, [routeLocation]);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (typingTimerRef.current) {
				clearTimeout(typingTimerRef.current);
			}
		};
	}, []);

	return (
		<>
			<div className={`col-span-12 hidden lg:block lg:col-span-3 lg:self-start lg:sticky md:col-span-12 md:hidden md:relative overflow-auto relative scroll-box sm:col-span-12 sm:hidden sm:relative ${position === 'right' ? 'lg:col-start-10' : ''}`}>

				<div className="relative  h-auto sm:h-auto md:h-auto lg:h-auto">
					{showProfile && (
						<div className="relative flex items-center gap-2">
							<div>
								{profileData &&
									<>
										{profilePicLoader ?
											<div style={{ position: 'absolute', top: '23px', left: '25px' }}>
												<ProfilePicLoader />
											</div>
											: null}


										<img src={profileData?.thumbnail} className=" w-16 h-16" alt="" ref={imageRef} />
									</>}

								<input type="file" accept="image/*" onChange={(e) => onHandleProfilePicChanger(e, setProfilePicLoader, imageRef, dispatch)} id="fileInput" ref={fileInputRef} className="hidden" />

								<div className="ot_takePictureBgSidebar rounded-t-none" onClick={() => fileInputRef.current.click()}>
									<ProfileCameraIcon />
								</div>

								<span className="bottom-0 absolute left-14 profile-activity-indicator  w-3.5 h-3.5 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></span>
							</div>

							<div className="">
								<h3 className="rubik-400 text-md">{profileData?.name?.toUpperCase()}</h3>
								{profileData?.type && <p className="text-sm">{profileData?.type?.charAt(0)?.toUpperCase() + profileData?.type?.slice(1)}</p>}
							</div>
						</div>
					)}

					<div className={`sidebarListActive ${showProfile ? 'mt-4' : ''}`}>
						{/* Render top content if provided */}
						{topContent && topContent}
						{children ? children :
						(routeLocation === routeNames.HOME_ROUTE || routeLocation === routeNames.DASHBOARD_ROUTE) || (routeLocation === routeNames.APPRECIATIONS_ROUTE) ||
							(routeLocation === routeNames.TIMESHEET_ROUTE) || (routeLocation === routeNames.NEWREQUEST_ROUTE) ||
							(routeLocation === routeNames.ALLREQUEST_ROUTE) || (routeLocation === routeNames.pharmacy_search_module) ||
							(routeLocation === routeNames.ALLEMPLOYEES_ROUTE) || (routeLocation === routeNames.EDIT_TIME_OFFR_REQUEST_ROUTE)
							|| (routeLocation === routeNames.MY_PROFILE) || (routeLocation === routeNames.pharmacy_all_notifications) ||
							(routeLocation === routeNames.pharmacy_file_manager) || (routeLocation === routeNames.pharmacy_reminder_notes) || (routeLocation === routeNames.pharmacy_delivered_rx_reasons) ||
							(routeLocation.startsWith(routeNames.EDIT_TIME_OFFR_REQUEST_ROUTE) || (routeLocation === routeNames.pharmacy_tasks_list) ||
								(routeLocation === routeNames.pharmacy_bookmarks))

							?
							<ul className="m-0 p-0">

								{constants?.DashboardItems?.map((value, index) => {
									return (<React.Fragment key={index}>

										{value.name === 'TEAM NOTES' ?
											<>
												<li className="py-2 px-0 grid -mt-2">
													<div className="container">
														<div className="relative rounded-md">
															<textarea
																value={addTeamNoteData}
																onChange={handalDashboardDataChange}
																style={{ background: 'lightyellow', height: '300px', paddingBottom: '50px' }}
																rows="14"
																className="rubik-400 fs-16 resize-none block p-2.5 w-full text-sm text-gray-900 bg-white rounded-md border border-gray-300 focus:ring-blue-500 
																focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
																placeholder="">
															</textarea>

															{teamNotesMessageResponse?.loading || loading ? null :
																addTeamNoteData !== teamNotesMessage && addTeamNoteData !== '' && (
																	<button
																		onClick={ HandleUpdateNote}
																		type="button"
																		className="glow-on-hover absolute bottom-2 left-2 px-4 py-1"
																	>
																		<span className="button-content">
																			<span className="default-text">SAVE ALL CHANGES</span>
																		</span>
																	</button>
																)
															}
														</div>
													</div>
												</li>
												<li className="py-2 px-0 grid">
													<NewRxCard />
												</li>
											</> : <li className="py-2 px-0 grid" onClick={() => { return navigate(value.route) }}>
												<a className={`${routeLocation === value.route ? "active" : ""} py-2 rounded-md w-full btnnn`}>
													{value.name.toUpperCase()}
												</a>
											</li>}

									</React.Fragment>
									)
								})}
							</ul> :

							(routeLocation === routeNames.SETTINGS_ROUTE) || (routeLocation === routeNames.pharmacy_front_profile) ||
								(routeLocation === routeNames.pharmacy_fax_log) || (routeLocation === routeNames.pharmacy_break_parroll_settings)
								|| (routeLocation === routeNames.pharmacy_settings) || (routeLocation === routeNames.pharmacy_holidays_calander)
								?
								<TabsSubMenuList data={constants?.SettingItems} />
								:
								routeLocation === routeNames.ADD_PATIENTS || routeLocation === routeNames.ALL_PATIENTS
									|| routeLocation === routeNames.pharmacy_patient_route || routeLocation === routeNames.pharmacy_add_patient_route
									?
									<ul className="pt-2 pb-4">
										{constants.PateintData.map((value, index) => {
											if (!navItemAllowed(value.route, hasModuleKey)) return null
											// Hidden when the pharmacy has Blister Patients switched off in admin.
											if (value.route?.includes('blister=1') && !blisterPatientsEnabled) return null

											const isActive = value.route === routeNames.ADD_PATIENTS
												? routeLocation === routeNames.ADD_PATIENTS :
												value.route === routeNames.pharmacy_patient_route ? routeLocation === routeNames.pharmacy_patient_route
													: currentStatus && currentStatus === getStatusFromPathname(value.route);

											return (
												<React.Fragment key={index}>
													<li className="py-2 px-0 grid" onClick={() => navigate(value.route)}>
														<a className={`py-2 rounded-md w-full btnnn ${isActive ? 'active' : ''}`}>
															{value.name.toUpperCase()}
														</a>
													</li>
												</React.Fragment>
											);
										})}

									</ul> :

									routeLocation === routeNames.PHARMACY_INFORMATION_UPDATE_FORM ||
										routeLocation === routeNames.PHARMACY_PRESCRIPTION_TRASNSFER_FORM ||
										routeLocation === routeNames.pharmacy_Emergency_Supply_OAT_medications_form ||
										routeLocation === routeNames.pharmacy_medication_form ||
										routeLocation === routeNames.missing_Rx_Requirements ||
										routeLocation === routeNames.pharmacy_emergency_supply_continuity_form ||
										routeLocation === routeNames.patient_agreement_for_take_home_dosing ||
										routeLocation === routeNames.pharmacy_Plan_G_Form ||
										routeLocation === routeNames.pharmacy_Plan_Z_Form ||
										routeLocation === routeNames.pharmacy_doctor_communication_form ||
										routeLocation === routeNames.pharmacy_Pharmacist_Prescriber_Communication ||
										routeLocation === routeNames.pharmacy_original_prescription_form ||
										routeLocation === routeNames.pharmacy_doctor_note_form ||
										routeLocation === routeNames.pharmacy_tb_skin_test_logs ||
										routeLocation === routeNames.pharmacy_tb_skin_test_mantoux_form ||
										routeLocation === routeNames.pharmacy_tb_form ||
										routeLocation === routeNames.pharmacy_tb_screening_form_logs

										?
										<TabsSubMenuList data={constants?.FormsData} />
										:
										routeLocation === routeNames.pharmacy_compounds ||
											routeLocation === routeNames.pharmacy_compound_agreement_form ||
											routeLocation === routeNames.pharmacy_controlled_order_request ||
											routeLocation === routeNames.pharmacy_order_compound_form ||
											routeLocation === routeNames?.pharmacy_order_compounding_form ||
											routeLocation === routeNames.pharmacy_compound_quotations ||
											routeLocation === routeNames.pharmacy_compound_dins ||
											routeLocation === routeNames.pharmacy_compound_logs ||
											routeLocation === routeNames.pharmacy_compound_due_billing ||
											routeLocation.startsWith(routeNames.pharmacy_compound_price_list) ?

											<ul className="pt-2 pb-4">
												{constants?.compoundItems?.map((value, index) => {
													// Sub-module gate ANDed with the existing
													// pharmacy_compound_price_list flag below.
													if (!navItemAllowed(value.route, hasModuleKey)) return null

													if (value.name === 'PRICE LIST' && pharmacyLoginUser?.pharmacy_compound_price_list === value.PermissionStatus) {
														return (
															<li className="mb-2 mr-2 grid" key={index} onClick={() => navigate(value.route)}>
																<a className={`${routeLocation === value.route ? "active" : ""} py-2 rounded-md w-full btnnn`}>
																	{value.name.toUpperCase()}
																</a>
															</li>
														);
													} else if (value.name !== 'PRICE LIST') {
														return (
															<li className="mb-2 mr-2 grid" key={index} onClick={() => navigate(value.route)}>
																<a className={`${routeLocation === value.route ? "active" : ""} py-2 rounded-md w-full btnnn`}>
																	{value.name.toUpperCase()}
																</a>
															</li>
														);
													} else {
														return null;
													}
												})}
											</ul>
											:
											routeLocation === routeNames.pharmacy_create_manual_delivery ||
												routeLocation === routeNames.pharmacy_route_delivery ||
												routeLocation === routeNames.pharmacy_driver ||
												routeLocation === routeNames.pharmacy_deliveries_supervisor ||
												routeLocation === routeNames.pharmacy_delivery ||
												routeLocation === routeNames.pharmacy_missed_deliveries ||
												routeLocation === routeNames.pharmacy_archived_deliveries ||
												routeLocation === routeNames.pharmacy_driver_clinic_call_logs ||
												routeLocation === routeNames.pharmacy_delivery_lables ||
												routeLocation === routeNames.pharmacy_patient_counselling ||
												routeLocation === routeNames.pharmacy_consent_form_logs ||
												routeLocation === routeNames.pharmacy_drugs_billing ||
												routeLocation === routeNames.pharmacy_oat_prescriptions ||
												routeLocation === routeNames.pharmacy_memo ||
												routeLocation === routeNames.upload_prescription ||
												routeLocation === routeNames.pharmacy_ongoing_prescritiptions ||
												routeLocation === routeNames.pharmacy_returned_patches ||
												routeLocation === routeNames.pharmacy_reversed_deliveries ||
												routeLocation === routeNames.pharmacy_expiring_rx ||
												routeLocation === routeNames.pharmacy_expiring_rx_v2 ?

												<TabsSubMenuList data={constants?.DispenseItems} />
												:

												routeLocation === routeNames.pharmacy_patient_blister_pack ||
													routeLocation === routeNames.pharmacy_blister_patients ||
													routeLocation === routeNames.pharmacy_blister_packs ||
													routeLocation === routeNames.pharmacy_blister_daily_packs ||
													routeLocation === routeNames.pharmacy_blister_pack_on_shelf
													?
													<TabsSubMenuList data={constants?.BlistersItems} />

													: routeLocation === routeNames.pharmacy_patient_vials_pack ||
														routeLocation === routeNames.pharmacy_add_vials_patient ||
														routeLocation === routeNames.pharmacy_vials_packs ||
														routeLocation === routeNames.pharmacy_dailyvials_packs
														?
														<TabsSubMenuList data={constants?.AddVialsItems} />
														:
														routeLocation === routeNames.pharmacy_add_medication_patients ||
															routeLocation === routeNames.pharmacy_speciality_pack ||
															routeLocation === routeNames.pharmacy_speciality_medication_packs ||
															routeLocation === routeNames.pharmacy_dailymedication_packs
															?
															<TabsSubMenuList data={constants?.MedicationItems} />

															: routeLocation === routeNames.pharmacy_generate_invoice ||
																routeLocation === routeNames.pharmacy_previous_invoices
																?
																<TabsSubMenuList data={constants?.InvoiceItems} />
																:
																routeLocation === routeNames.ADD_ARV ||
																	routeLocation === routeNames.ARV_LIST
																	?
																	<TabsSubMenuList data={constants?.ARVItems} />

																	: routeLocation === routeNames.pharmacy_daily_revenue ||
																		routeLocation === routeNames.pharmacy_cash_change ||
																		routeLocation === routeNames.pharmacy_deposit_info ||
																		routeLocation === routeNames.pharmacy_hand_cash
																		?
																		<TabsSubMenuList data={constants?.RevenueItems} />

																		: routeLocation === routeNames.create_new_fda_form ||
																			routeLocation === routeNames.all_generated_fda_form_list ||
																			routeLocation === routeNames.pharmacy_expiring_frequent_dispense ||
																			routeLocation === routeNames.pharmacy_expired_frequent_dispense ||
																			routeLocation === routeNames.pharmacy_fda_audit ||
																			routeLocation === routeNames.pharmacy_frequent_dispensing_authorization_timeline
																			?
																			<TabsSubMenuList data={constants?.FdaFormsItems} />

																			: routeLocation === routeNames.pharmacy_inventory_list ||
																				routeLocation === routeNames.pharmacy_inventory_accountability ||
																				routeLocation === routeNames.pharmacy_fridge_temperature_log ||
																				routeLocation === routeNames.pharmacy_fentanyl_patch_log ||
																				routeLocation === routeNames.pharmacy_total_reversed
																				?
																				<TabsSubMenuList data={constants?.InventoryItems} />
																				:
																				routeLocation === routeNames.pharmacy_add_travel_pack ||
																					routeLocation === routeNames.pharmacy_add_travel_patient ||
																					routeLocation === routeNames.pharmacy_travel_logs ||
																					routeLocation === routeNames.pharmacy_billing_due ||
																					routeLocation === routeNames.pharmacy_travel_returning_patients
																					?
																					<TabsSubMenuList data={constants?.AddTravelItems} />
																					:
																					routeLocation === routeNames.pharmacy_app_prescriptions ||
																						routeLocation === routeNames.pharmacy_order_transfer ||
																						routeLocation === routeNames.pharmacy_order_new ||
																						routeLocation === routeNames.pharmacy_app_user_list ||
																						routeLocation === routeNames.pharmacy_app_announcements ||
																						routeLocation === routeNames.pharmacy_app_notification_list ||
																						routeLocation === routeNames.pharmacy_app_pharmacy_services ||
																						routeLocation === routeNames.pharmacy_app_pharmacy_services_alias ||
																						routeLocation.startsWith(routeNames.pharmacy_view_order_details) ||
																						routeLocation === routeNames.pharmacy_app_refill_request ||
																						routeLocation === routeNames.pharmacy_app_patient_chat
																						?
																						<TabsSubMenuList data={constants?.AppItems} />
																						:
																						routeLocation === routeNames.pharmacy_timeoff ||
																							routeLocation === routeNames.pharmacy_create_new_task ||
																							routeLocation === routeNames.pharmacy_tasks ||
																							routeLocation === routeNames.pharmacy_certificate_template ||
																							routeLocation === routeNames.pharmacy_team_attendance ||
																							routeLocation === routeNames.pharmacy_check_out_in ||
																							routeLocation === routeNames.pharmacy_assign_certificate ||
																							routeLocation === routeNames.pharmacy_department ||
																							routeLocation === routeNames.pharmacy_user ||
																							routeLocation === routeNames.pharmacy_team_avalibility
																							?
																							<TabsSubMenuList data={constants?.TeamItems} /> :

																							routeLocation === routeNames.pharmacy_juno_appointment ||
																								routeLocation === routeNames.pharmacy_appointment_logs ?
																								<TabsSubMenuList data={constants?.AppointmentsItems} />

																								:
																								routeLocation === routeNames.pharmacy_vaccine_passport || routeLocation === routeNames.pharmacy_vaccine_logs ?
																									<TabsSubMenuList data={constants?.VccineItems} />

																									: null
						}
					</div>
				</div>
			</div>

		</>
	)
}

export default Sidebar;
